/**
 * API Client with automatic token refresh on 401 errors
 */

const API_URL = process.env.REACT_APP_API_BASE_URL ||
                process.env.REACT_APP_BACKEND_URL ||
                'https://cyberguard-backend-wa00.onrender.com';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Make an authenticated API request with automatic token refresh
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');

  // Add Authorization header if token exists
  if (token) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };
  }

  // Make the initial request
  let response = await fetch(url, options);

  // If 401, try to refresh token
  if (response.status === 401) {
    if (isRefreshing) {
      // Wait for the ongoing refresh to complete
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(newToken => {
        options.headers['Authorization'] = `Bearer ${newToken}`;
        return fetch(url, options);
      });
    }

    isRefreshing = true;

    try {
      const refresh_token = localStorage.getItem('refresh_token');

      if (!refresh_token) {
        throw new Error('No refresh token available');
      }

      // Attempt token refresh
      const refreshResponse = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token }),
      });

      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        const newToken = data.access_token;

        // Store new token
        localStorage.setItem('token', newToken);

        // Update authorization header and retry original request
        options.headers['Authorization'] = `Bearer ${newToken}`;

        // Process queued requests
        processQueue(null, newToken);

        console.log('✅ Token refreshed, retrying request');

        // Retry the original request
        response = await fetch(url, options);
      } else {
        // Refresh failed - clear auth and redirect to login
        console.error('❌ Token refresh failed, redirecting to login');
        localStorage.removeItem('cyberguard_user');
        localStorage.removeItem('cyberguard_login_time');
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');

        processQueue(new Error('Token refresh failed'), null);

        // Trigger page reload to show login screen
        window.location.href = '/';
      }
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      processQueue(error, null);

      // Clear auth and redirect to login
      localStorage.removeItem('cyberguard_user');
      localStorage.removeItem('cyberguard_login_time');
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/';
    } finally {
      isRefreshing = false;
    }
  }

  return response;
};

export default authenticatedFetch;
